import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import { adminOrderService } from "../../services/admin.order.service";
import { OrderData, OrderHistoryEvent, orderService, ExternalReferenceTrack, GetOrderHistoryResponse } from "../../services/order.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faTruck,
  faExclamationTriangle,
  faChevronLeft,
  faFileDownload,
  faTags,
  faMapMarkerAlt,
  faBoxOpen,
  faInfoCircle,
  faTimes,
  faWeightHanging,
  faCoins,
  faArrowDown,
  faQuestionCircle,
  faChevronDown,
  faChevronUp,
  faCheck,
  faCheckCircle,
  faReceipt,
  faHistory,
  faUser,
  faBatteryFull,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { errorService } from "../../services/error.service";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { permissionCheckerService } from "../../services/permission.checker.service";
import logoBoxIMG from "../../assets/images/logo-box.png";
import styles from "../../styles/app/OrderDetailPage.module.css";
import AddressSelectionCard from "../../components/common/AddressSelectionCard";
import { Address } from "../../services/customer.address.service";
import DashboardCard from "../../components/common/DashboardCard";
import ServiceCard from "../../components/common/ServiceCard";
import { RateOption } from "../../services/rate.service";
import ServiceDetailModalV2 from "../../components/common/ServiceDetailModalV2";
import { countryService } from "../../services/country.service";

const AdminOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { adminData } = useAdminAuth();
  const canCancel = permissionCheckerService.checkPermission(adminData, "ORDERS", "CANCEL");

  const [order, setOrder] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [orderHistory, setOrderHistory] = useState<GetOrderHistoryResponse['data'] | null>(null);
  const [externalRefTrack, setExternalRefTrack] = useState<ExternalReferenceTrack | null>(null);
  const [activeSection, setActiveSection] = useState<'carrier' | 'external' | null>(null);
  const externalSectionRef = useRef<HTMLDivElement>(null);
  const carrierSectionRef = useRef<HTMLDivElement>(null);
  const [countryNamesMap, setCountryNamesMap] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await countryService.getAllCountries();
        const map: { [key: string]: string } = {};
        data.forEach((c) => {
          if (c.iso2Country) {
            map[c.iso2Country.toUpperCase()] = c.name;
          }
        });
        setCountryNamesMap(map);
      } catch (err) {
        console.error("Error fetching country names in AdminOrderDetailPage:", err);
      }
    };
    fetchCountries();
  }, []);

  const getCountryName = (code: string) => {
    if (!code) return "";
    const uppercaseCode = code.toUpperCase();
    return countryNamesMap[uppercaseCode] || code;
  };

  useEffect(() => {
    if (activeSection === 'external' && externalSectionRef.current) {
      externalSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else if (activeSection === 'carrier' && carrierSectionRef.current) {
      carrierSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeSection]);
  const handleToggleHistory = () => {
    setIsHistoryOpen(!isHistoryOpen);
  };

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const response = await adminOrderService.getOrderResume(parseInt(id, 10));
        if (response.statusCode === 200) {
          setOrder(response.data);
          try {
            const historyRes = await orderService.getOrderHistoryById(id);
            if (historyRes.statusCode === 200) {
              setOrderHistory(historyRes.data);
              if (historyRes.externalReferenceTrack) {
                setExternalRefTrack(historyRes.externalReferenceTrack);
              }
            }
          } catch (err) {
            console.error("Error fetching order history:", err);
          }
        } else {
          errorService.showErrorAlert(response, "Error");
        }
      } catch (error) {
        console.error("Error fetching order:", error);
        errorService.showErrorAlert(error, "Error");
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const handleCancelOrder = async () => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción cancelará el envío seleccionado.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No, volver",
    });

    if (result.isConfirmed && order) {
      try {
        setLoading(true);
        const response = await adminOrderService.cancelOrder({ Id: order.id });

        if (response.statusCode === 200) {
          await Swal.fire({
            icon: "success",
            title: "Cancelada",
            text: "La orden ha sido cancelada con éxito.",
          });
          navigate(-1);
        } else {
          errorService.showErrorAlert(response, "Error");
        }
      } catch (err) {
        console.error("Error cancelling order:", err);
        errorService.showErrorAlert(err, "Error");
      } finally {
        setLoading(false);
      }
    }
  };

  const downloadPdf = (base64: string, fileName: string) => {
    try {
      const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;
      const linkSource = `data:application/pdf;base64,${base64Data}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    } catch (error) {
      console.error("Error downloading PDF:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un problema al intentar descargar el documento.",
      });
    }
  };

  const handleDownloadMainLabel = () => {
    if (!order) return;

    const labelsToDownload: { base64: string; fileName: string }[] = [];

    // 1. Check if the main carrier has a base64 label
    if (order.base64Carrier) {
      labelsToDownload.push({
        base64: order.base64Carrier,
        fileName: `Guia_${order.tracking}.pdf`,
      });
    }

    // 2. Check all external service labels (e.g. UPS MP parcels in tbOrderExternalCourierReferences)
    if (order.orderExternalService && order.orderExternalService.length > 0) {
      order.orderExternalService.forEach((ext) => {
        if (ext.base64ExternalService && ext.codeRef !== "PRN") {
          const fileName = ext.externalReference
            ? `Guia_${ext.externalReference}.pdf`
            : `Guia_Servicio_${ext.id}.pdf`;
          if (!labelsToDownload.some((item) => item.base64 === ext.base64ExternalService)) {
            labelsToDownload.push({
              base64: ext.base64ExternalService,
              fileName: fileName,
            });
          }
        }
      });
    }

    if (labelsToDownload.length === 0) {
      Swal.fire({
        icon: "info",
        title: "Etiqueta no disponible",
        text: "No se encontró una etiqueta de envío para este caso.",
      });
      return;
    }

    labelsToDownload.forEach((item, index) => {
      setTimeout(() => {
        downloadPdf(item.base64, item.fileName);
      }, index * 400);
    });
  };

  const getParcelLabelBase64 = (p: any, idx: number): string | null => {
    if (!order) return null;
    const pTracking = p.tracking || order.tracking;

    if (order.orderExternalService && order.orderExternalService.length > 0) {
      const match = order.orderExternalService.find(
        (ext) => ext.externalReference === pTracking && ext.base64ExternalService
      );
      if (match?.base64ExternalService) {
        return match.base64ExternalService;
      }
      if (order.orderExternalService[idx]?.base64ExternalService) {
        return order.orderExternalService[idx].base64ExternalService;
      }
    }

    if (idx === 0 && order.base64Carrier) {
      return order.base64Carrier;
    }

    return null;
  };

  const mapOrderAddressToAddress = (
    orderAddr: any,
    nickname: string,
    type: string,
  ): Address => {
    return {
      id: 0,
      nickname: nickname,
      addressLine1: orderAddr?.addressLine || "",
      city: orderAddr?.city || "",
      state: orderAddr?.state || "",
      zipCode: orderAddr?.zipcode || "",
      isO2Country: orderAddr?.country || "",
      type: type,
      addressSubjet: {
        firstName: orderAddr?.firstName || "",
        lastName: orderAddr?.lastName || "",
        kindOfDNI: orderAddr?.kindOfDNI || "",
        dni: orderAddr?.dni || "",
        phoneNumber: orderAddr?.phoneNumber || "",
        email: "",
        isThirdPerson: !!orderAddr?.isThirdPerson,
      },
    };
  };

  const mapCustomerToAddress = (cust: any): Address => {
    return {
      id: 0,
      nickname: "Solicitante",
      addressLine1: "",
      city: "",
      state: "",
      zipCode: "",
      isO2Country: cust.country || "",
      type: "C",
      addressSubjet: {
        firstName: cust.firstName || "",
        lastName: cust.lastName || "",
        kindOfDNI: cust.kindOfDNI || "",
        dni: cust.dni || "",
        phoneNumber: cust.phoneNumber || "",
        email: "",
      },
    };
  };

  const mapOrderToRateOption = (order: OrderData): RateOption => {
    const allServices = order.parcelData.flatMap((parcel) =>
      parcel.parcelServices.map((ps) => ({
        serviceName: ps.serviceDetail,
        serviceQty: 1,
        servicePrice: ps.serviceCost,
        serviceSubTotal: ps.serviceCost,
        mandatory: true,
      })),
    );

    return {
      carrierName: order.carrierName,
      rate: order.serviceCostTotal,
      currency: order.isO3Currency,
      services: allServices,
      externalServices: [],
    };
  };

  if (loading) {
    return (
      <AdminDashboardLayout>
        <div className={styles["loading-container"]}></div>
      </AdminDashboardLayout>
    );
  }

  if (!order) {
    return (
      <AdminDashboardLayout>
        <div className={`${styles["order-detail-container"]} text-center`}>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            size="4x"
            className="text-warning mb-4"
          />
          <h3>No se encontró el envío</h3>
          <p>
            No pudimos encontrar información para el ID:{" "}
            <b>{id}</b>
          </p>
          <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faChevronLeft} /> Volver
          </button>
        </div>
      </AdminDashboardLayout>
    );
  }

  const parcel = order.parcelData?.[0];
  const hasCarrierLabel = !!order.base64Carrier;
  const hasAnyExternalLabel = order.orderExternalService?.some(
    (ext) => ext.base64ExternalService,
  );

  return (
    <AdminDashboardLayout>
      <div className={styles["order-detail-container"]}>

        {/* ── Header DashboardCard ── */}
        <div className={styles["order-detail-header"]}>
          <DashboardCard
            icon={faBoxOpen}
            title="Detalles del Envío"
            description="Información completa de tu paquete y servicios asociados (Modo Administrador)."
            showVerificationAlerts={false}
          />
        </div>

        {/* ── Badges row: estado | tracking | cancelar ── */}
        <div className={styles["header-badges-container"]}>
          <div className={styles["header-left-badges"]}>
            <div className={styles["status-badge"]}>
              <FontAwesomeIcon icon={faTruck} />
              {order.lastEventStatus ?? "—"}
            </div>
            <div className={styles["tracking-group"]}>
              <div className={`${styles["tracking-badge"]} ${styles["main-tracking"]}`}>
                TRACKING: {order.tracking}
              </div>
              <button
                className={styles["icon-action-btn"]}
                onClick={handleDownloadMainLabel}
                title="Descargar Etiqueta de Envío"
              >
                <FontAwesomeIcon icon={faFileDownload} />
              </button>
              <Link
                to={`/instructive?showStep2=${order.orderExternalService?.some((ext) => ext.base64ExternalService) || false}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles["icon-action-btn"]} ${styles["icon-info-btn"]}`}
                title="¿Cómo usarlas?"
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
              </Link>
            </div>
          </div>
          {canCancel && (
            <button
              className={styles["cancel-order-btn"]}
              onClick={handleCancelOrder}
              disabled={false}
              title="Cancelar orden"
            >
              <FontAwesomeIcon icon={faTimes} />
              Cancelar Orden
            </button>
          )}
        </div>

        {/* ── Quick summary bar ── */}
        {order.parcelData && order.parcelData.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "16px" }}>
            {order.parcelData.map((p, idx) => {
              const parcelBase64 = getParcelLabelBase64(p, idx);
              return (
                <div key={p.id || idx} className={styles["info-summary-bar"]} style={{ position: "relative" }}>
                  {parcelBase64 && order.parcelData.length > 1 && (
                    <button
                      type="button"
                      className={styles["icon-action-btn"]}
                      style={{ position: "absolute", top: "12px", right: "12px", zIndex: 2 }}
                      onClick={() => downloadPdf(parcelBase64, `Guia_${p.tracking || order.tracking || `Pieza_${idx + 1}`}.pdf`)}
                      title="Descargar Guía de esta pieza"
                    >
                      <FontAwesomeIcon icon={faFileDownload} />
                    </button>
                  )}
                  {order.parcelData.length > 1 && (
                    <div className={styles["info-summary-chip"]}>
                      <span className={styles["info-chip-label"]}>Pieza #{idx + 1}</span>
                      <span className={styles["info-chip-value"]}>{p.tracking || order.tracking}</span>
                    </div>
                  )}
                  <div className={styles["info-summary-chip"]}>
                    <span className={styles["info-chip-label"]}>
                      <FontAwesomeIcon icon={faWeightHanging} style={{ marginRight: 4 }} />
                      Peso
                    </span>
                    <span className={styles["info-chip-value"]}>
                      {p.weight} {p.weightUnit}
                    </span>
                  </div>
                  <div className={styles["info-summary-chip"]}>
                    <span className={styles["info-chip-label"]}>
                      <FontAwesomeIcon icon={faCoins} style={{ marginRight: 4 }} />
                      Valor Declarado (FOB)
                    </span>
                    <span className={styles["info-chip-value"]}>
                      ${p.fob} {p.isO3Currency}
                    </span>
                  </div>
                  <div className={styles["info-summary-chip"]}>
                    <span className={styles["info-chip-label"]}>Dimensiones (Pulgadas)</span>
                    <span className={styles["info-chip-value"]}>
                      L {p.length} × An {p.width} × Al {p.height} {p.sizeUnit}
                    </span>
                  </div>
                  <div className={styles["info-summary-chip"]}>
                    <span className={styles["info-chip-label"]}>Courier</span>
                    <span className={styles["info-chip-value"]}>{order.carrierName}</span>
                  </div>
                  <div className={styles["info-summary-chip"]}>
                    <span className={styles["info-chip-label"]}>Contenido</span>
                    <span className={styles["info-chip-value"]}>{p.content || "Sin descripción"}</span>
                  </div>
                  <div className={styles["info-summary-chip"]}>
                    <span className={styles["info-chip-label"]}>
                      <FontAwesomeIcon icon={faBatteryFull} style={{ marginRight: 4 }} />
                      Baterías
                    </span>
                    <span className={styles["info-chip-value"]}>
                      {order.hasBattery ? "SÍ" : "NO"}
                    </span>
                  </div>
                  <div className={styles["info-summary-chip"]}>
                    <span className={styles["info-chip-label"]}>
                      <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 4 }} />
                      Frágil
                    </span>
                    <span className={styles["info-chip-value"]} style={{ color: p.isFragile ? "#dc2626" : undefined, fontWeight: p.isFragile ? 700 : undefined }}>
                      {p.isFragile ? "SÍ" : "NO"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Paquete ── */}
        {order.parcelData && order.parcelData.length > 0 && parcel && (() => {
          return (
            <>
              {externalRefTrack && (
                <div ref={externalSectionRef} style={{ marginBottom: '16px', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', background: '#ffffff' }}>
                  <div
                    onClick={() => setActiveSection(activeSection === 'external' ? null : 'external')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: '#f8fafc',
                      cursor: 'pointer',
                      borderBottom: activeSection === 'external' ? '1px solid #e2e8f0' : 'none',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                      <span style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                        Seguimiento {externalRefTrack.serviceName}
                      </span>
                      <span style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', fontWeight: 500 }}>
                        Referencia: {externalRefTrack.externalReference}
                      </span>
                    </div>
                    <FontAwesomeIcon icon={activeSection === 'external' ? faChevronUp : faChevronDown} style={{ color: '#64748b' }} />
                  </div>

                  {activeSection === 'external' && (
                    <div style={{ padding: '16px' }}>
                      <div className={styles.timeline}>
                        {externalRefTrack.events.map((event, index) => (
                          <div key={index} className={styles.timelineItem}>
                            <div className={styles.timelineDot}>
                              <FontAwesomeIcon
                                icon={faCheckCircle}
                                color={index === 0 ? "var(--brand-black)" : "#cbd5e1"}
                              />
                            </div>
                            <div className={styles.timelineContent}>
                              <div className={styles.statusTitle}>{event.event}</div>
                              {event.location && (
                                <div className={styles.statusLocation}>
                                  <FontAwesomeIcon icon={faMapMarkerAlt} size="xs" style={{ marginRight: 4 }} />
                                  {event.location}
                                </div>
                              )}
                              <div className={styles.statusDate}>
                                {event.date}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {orderHistory && (
                <div ref={carrierSectionRef} style={{ marginBottom: '24px', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', background: '#ffffff' }}>
                  <div
                    onClick={() => setActiveSection(activeSection === 'carrier' ? null : 'carrier')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: '#f8fafc',
                      cursor: 'pointer',
                      borderBottom: activeSection === 'carrier' ? '1px solid #e2e8f0' : 'none',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                      <span style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                        Seguimiento {orderHistory.carrierName}
                      </span>
                      <span style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', fontWeight: 500 }}>
                        Referencia: {orderHistory.carrierTracking}
                      </span>
                    </div>
                    <FontAwesomeIcon icon={activeSection === 'carrier' ? faChevronUp : faChevronDown} style={{ color: '#64748b' }} />
                  </div>

                  {activeSection === 'carrier' && (
                    <div style={{ padding: '16px' }}>
                      <div className={styles.timeline}>
                        {orderHistory.events && orderHistory.events.length > 0 ? (
                          orderHistory.events.map((event, index) => (
                            <div key={index} className={styles.timelineItem}>
                              <div className={styles.timelineDot}>
                                <FontAwesomeIcon
                                  icon={faCheckCircle}
                                  color={index === 0 ? "var(--brand-black)" : "#cbd5e1"}
                                />
                              </div>
                              <div className={styles.timelineContent}>
                                <div className={styles.statusTitle}>{event.eventStatus}</div>
                                <div className={styles.statusDate}>
                                  {new Date(event.date).toLocaleDateString()} - {new Date(event.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-muted"><small>No hay eventos disponibles.</small></div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Direcciones */}
              <div className={styles["order-card"]}>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h4 className="mb-0">
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> Direcciones
                  </h4>
                  {(() => {
                    const prnService = order.orderExternalService?.find(ext => ext.codeRef === "PRN");
                    if (prnService) {
                      return (
                        <div className={styles["prn-badge"]}>
                          <FontAwesomeIcon icon={faBox} className="me-1" />
                          PRN: {prnService.externalReference}
                          <div className={styles["tooltip-container"]}>
                            <span className={styles["tooltip-icon"]}>i</span>
                            <div className={styles["tooltip-text"]}>
                              Referencia del PickUp
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div className={styles["order-address-grid"]}>
                  <div className="row g-2">
                    {order.customer && (
                      <div className="col-lg-4 col-md-6 d-flex flex-column">
                        <div
                          className="fw-bold mb-3 mt-2 small"
                          style={{ lineHeight: "1.2", color: "var(--brand-black)" }}
                        >
                          Solicitante
                        </div>
                        <AddressSelectionCard
                          isSelected={false}
                          address={mapCustomerToAddress(order.customer)}
                          onClick={() => { }}
                          icon={faUser}
                        />
                      </div>
                    )}
                    <div className={`${order.customer ? "col-lg-4 col-md-6" : "col-md-6"} d-flex flex-column`}>
                      <div
                        className="fw-bold mb-3 mt-2 small"
                        style={{ lineHeight: "1.2", color: "var(--brand-black)" }}
                      >
                        País de orígen: {order.sender?.country || "N/A"}
                      </div>
                      <AddressSelectionCard
                        isSelected={false}
                        address={mapOrderAddressToAddress(order.sender, "Remitente", "Origin")}
                        onClick={() => { }}
                        icon={faMapMarkerAlt}
                      />
                    </div>
                    <div className={`${order.customer ? "col-lg-4 col-md-6" : "col-md-6"} d-flex flex-column`}>
                      <div
                        className="fw-bold mb-3 mt-2 small"
                        style={{ lineHeight: "1.2", color: "var(--brand-black)" }}
                      >
                        País destino: {order.receiver?.country || "N/A"}
                      </div>
                      <AddressSelectionCard
                        isSelected={false}
                        address={mapOrderAddressToAddress(order.receiver, "Destinatario", "Destination")}
                        onClick={() => { }}
                        icon={faMapMarkerAlt}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Desglose de Pago */}
              <div className={styles["order-card"]}>
                <div className="row">
                  <div className="col-12">
                    <h4 className="mb-3">
                      <FontAwesomeIcon icon={faReceipt} /> Desglose de Pago
                    </h4>

                    <div className={styles["invoice-container"]}>
                      {mapOrderToRateOption(order).services.map((svc, i, arr) => (
                        <div key={i} className={`${styles["invoice-row"]} ${i === arr.length - 1 ? styles["last-invoice-row"] : ""}`}>
                          <span className={styles["invoice-label"]}>{svc.serviceName}</span>
                          <span className={styles["invoice-value"]}>
                            ${svc.servicePrice?.toFixed(2)} {order.isO3Currency}
                          </span>
                        </div>
                      ))}
                      <div className={styles["invoice-total-row"]}>
                        <span>Total Pagado</span>
                        <span>
                          ${order.serviceCostTotal?.toFixed(2)} {order.isO3Currency}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </AdminDashboardLayout>
  );
};

export default AdminOrderDetailPage;
